import { w3cwebsocket as W3CWebSocket } from "websocket"; // ESM (Vite has no CJS require in the browser)
import eventEmitter from "./utils/eventEmitter";
import { getWidgetToken } from "./utils/widgetStorage";
import { socketUrl } from "./configs/http-config";

class SocketClient {
	#ws = null;
	static instance = null;
	heartbeatInterval = null;
	reconnectInProgress = false;

	constructor(tenantId, contactId, botId) {
		if (SocketClient.instance) return SocketClient.instance;

		// contactId is the raw ID (sessionId or uuid) - socket URL appends _botId. The caller
		// passes all three; nothing writes them to localStorage, so there is no fallback to read.
		this.tenantId = tenantId;
		this.contactId = contactId;
		this.botId = botId;

		if (!this.tenantId || !this.contactId || !this.botId) {
			console.error("@@ chat-widget @@ Missing required params to initialize socket.");
			return;
		}

		this._lastHeartbeat = Date.now();
		this._hasConnectedBefore = false;
		this._reconnectAttempts = 0;

		SocketClient.instance = this;
		this.initializeSocket();
		this.startSleepWatcher();
		this.handleVisibilityChange();
		this.handleOnlineChange();
	}

	initializeSocket() {
		try {
			// Both sides of the 2026-08-28 sync merged ON PURPOSE:
			//  - upstream's socketUrl() resolves the socket HOST per environment (stage/prod/localhost)
			//    and appends the one path socket-service serves (`/socket-service/api/v1`) — a
			//    bare-root upgrade is refused with `unrecognized_path` before the origin is read
			//  - our widget identity token append stays (server-minted at bootstrap; inert until
			//    WIDGET_TOKEN_SECRET is provisioned on socket-service — the enforcement client half).
			const widgetToken = getWidgetToken();
			let url = `${socketUrl()}?origin=chat-widget&tenantId=${this.tenantId}&contactId=${this.contactId}_${this.botId}`;
			if (widgetToken) {
				url += `&widgetToken=${encodeURIComponent(widgetToken)}`;
			}
			this.#ws = new W3CWebSocket(url);

			this.setEventListeners();

			// Heartbeat: send ping every 15s (matching server interval) and
			// check that we've heard from the server within the last 40s.
			this._lastHeartbeat = Date.now();
			this.heartbeatInterval = setInterval(() => {
				if (!this.#ws || this.#ws.readyState !== this.#ws.OPEN) return;

				// Server pings every 15s. If we haven't heard from it in 40s, the
				// connection is a zombie (TCP open, but no traffic getting through).
				if (Date.now() - this._lastHeartbeat > 40000) {
					console.warn("@@ chat-widget @@ No server heartbeat in 40s, forcing reconnect");
					this.cleanupListeners();
					this.#ws.close();
					this.reconnect();
					return;
				}

				this.#ws.send(JSON.stringify({ topic: "ping" }));
			}, 15000);
		} catch (error) {
			console.error("@@ chat-widget @@ socket exception", error);
		}
	}

	setEventListeners() {
		this.#ws.onopen = () => {
			const isReconnect = this._hasConnectedBefore;
			this._hasConnectedBefore = true;
			// Successful connection -> reset backoff so the next drop starts at base.
			this._reconnectAttempts = 0;
			console.warn("@@ chat-widget @@ WebSocket connection established", isReconnect ? "(reconnect)" : "(initial)");
			if (isReconnect) {
				eventEmitter.emit("socket_reconnected");
			}
		};

		this.#ws.onclose = (event) => {
			this.cleanupListeners();
			if (event.reason === 'removing_old_connection') {
				console.warn("@@ chat-widget @@ Removing old connection without reconnect");
				return;
			}
			console.warn("@@ chat-widget @@ socket closed, reconnecting", event);
			this.reconnect();
		};

		this.#ws.onerror = (error) => {
			console.error('@@ chat-widget @@ socket error:', error.message);
			this.cleanupListeners();
			// Do not call reconnect here, onclose will handle it
		};

		this.#ws.onmessage = (event) => {
			try {
				const { topic, message } = JSON.parse(event.data);
				switch (topic) {
				case "ping":
					this._lastHeartbeat = Date.now();
					this.#ws.send(JSON.stringify({
						topic: "pong",
						message: { tenantId: this.tenantId, contactId: this.contactId }
					}));
					eventEmitter.emit("agents_update", { ...message });
					break;
				case "record_update_stream":
					eventEmitter.emit(topic, { ...message });
					break;
				case "record_update":
				case "assistant_typing":
				case "ticket_created":
				case "contact_update":
				case "conversation_assignee":
				case "conversation_status_update":
					eventEmitter.emit(topic, { ...message });
					break;
				default:
					console.warn("@@ chat-widget @@ Unhandled socket topic:", topic);
				}
			} catch (error) {
				console.error("@@ Error: onmessage @@", error);
			}
		};
	}

	cleanupListeners() {
		if (this.#ws) {
			this.#ws.onopen = null;
			this.#ws.onclose = null;
			this.#ws.onerror = null;
			this.#ws.onmessage = null;
		}
		clearInterval(this.heartbeatInterval);
	}

	reconnect() {
		if (this.reconnectInProgress) return;

		this.reconnectInProgress = true;
		if (this.#ws) {
			this.#ws.close();
		}

		const delay = this._nextReconnectDelay();
		setTimeout(() => {
			console.warn(`@@ chat-widget @@ Reconnecting to socket... (attempt ${this._reconnectAttempts}, delay ${delay}ms)`);
			this.initializeSocket();
			this.reconnectInProgress = false;
		}, delay);
	}

	/**
	 * Exponential backoff with jitter so a socket-service pod restart doesn't
	 * produce a synchronized reconnect spike across every widget.
	 * base 1s, x2 per attempt, capped at 30s, +/-20% jitter. Reset on a
	 * successful open (see onopen).
	 */
	_nextReconnectDelay() {
		const base = 1000;
		const max = 30000;
		const attempt = this._reconnectAttempts;
		this._reconnectAttempts = attempt + 1;
		const capped = Math.min(base * Math.pow(2, attempt), max);
		const jitter = capped * 0.2 * (Math.random() * 2 - 1); // +/-20%
		return Math.max(0, Math.round(capped + jitter));
	}

	startSleepWatcher() {
		let lastCheck = Date.now();
		setInterval(() => {
			const now = Date.now();
			if (now - lastCheck > 7000) {
				console.warn("@@ chat-widget @@ Detected wake from sleep. Reconnecting...");
				this.reconnect();
			}
			lastCheck = now;
		}, 5000);
	}

	handleVisibilityChange() {
		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'visible') {
				if (!this.#ws || this.#ws.readyState !== this.#ws.OPEN) {
					console.warn("@@ chat-widget @@ Tab visible again, reconnecting...");
					this.reconnect();
				}
			}
		});
	}

	handleOnlineChange() {
		window.addEventListener('online', () => {
			console.warn("@@ chat-widget @@ Network restored, forcing reconnect");
			this.cleanupListeners();
			if (this.#ws) this.#ws.close();
			this.reconnect();
		});
	}

	get client() {
		return this.#ws;
	}

	/**
	 * Destroys the socket connection and cleans up.
	 */
	destroy() {
		this.cleanupListeners();
		if (this.#ws) {
			this.#ws.close();
		}
	}

	/**
	 * Destroys the current socket instance to allow reconnection with new credentials.
	 */
	static destroyInstance() {
		if (SocketClient.instance) {
			SocketClient.instance.destroy();
			SocketClient.instance = null;
		}
	}

	static getInstance(tenantId, contactId, botId) {
		if (!SocketClient.instance) {
			SocketClient.instance = new SocketClient(tenantId, contactId, botId);
		}
		return SocketClient.instance;
	}
}

export default SocketClient;
